import Switch from "@/lib/components/ui/switch/switch";
import Text from "@/lib/components/ui/text/text";
import { cn } from "@/lib/utils";
import Tooltip, {
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/ui/tooltip/tooltip";
import { ChevronRight, CircleHelp, Lock, Pencil } from "lucide-react";
import type { ReactNode } from "react";

// the one look for every settings page, all row
// text at the app's normal 14px size: a heading
// over a card, the card split into rows, every row a title and a short
// description on the left with its control on the right or below. the card
// takes its border and shadow from the connector card, without its background

/**
 * settingsCardClassName is the card frame, for pages that build their own rows.
 * It clips instead of hiding overflow, so a sticky header inside still sticks.
 */
export const settingsCardClassName =
  "border-border divide-border flex flex-col divide-y overflow-clip rounded-xl border shadow-sm";

/** RowCard is a titled card whose children are rows split by lines. */
export function RowCard({
  title,
  actions,
  children,
}: {
  /** Heading above the card; leave it out for a card that stands alone */
  title?: string;
  /** Small icon actions at the end of the heading line */
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      {title ? (
        <div className="flex items-center justify-between gap-4">
          <Text as="h2" variant="title" size="sm">
            {title}
          </Text>
          {actions ? (
            <div className="flex shrink-0 items-center gap-1">{actions}</div>
          ) : null}
        </div>
      ) : null}
      <div className={settingsCardClassName}>{children}</div>
    </section>
  );
}

/** RowText is the title and description every row starts with. */
export function RowText({
  title,
  description,
  htmlFor,
  help,
}: {
  title: string;
  description?: ReactNode;
  /** Links the title to the row's input, so clicking it focuses the input */
  htmlFor?: string;
  /** Explains the row behind a question mark next to the title */
  help?: string;
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <div className="flex items-center gap-1.5">
        {htmlFor ? (
          <label htmlFor={htmlFor} className="text-sm font-medium">
            {title}
          </label>
        ) : (
          <Text className="font-medium">{title}</Text>
        )}
        {help ? <HelpHint text={help} /> : null}
      </div>
      {description ? (
        <Text className="text-foreground-pressed">{description}</Text>
      ) : null}
    </div>
  );
}

/** HelpHint is a question mark that explains something on hover. */
export function HelpHint({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label={text}
          className="text-foreground-pressed hover:text-foreground inline-flex shrink-0 cursor-help"
        >
          <CircleHelp className="size-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-72 whitespace-pre-line">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * LinkRow opens another page, showing the current value next to the arrow.
 * A row that edits in a dialog shows a pencil instead, so the arrow always
 * means "there is a page behind this".
 */
export function LinkRow({
  title,
  description,
  value,
  nested = false,
  locked = false,
  disabled = false,
  opensDialog = false,
  help,
  onClick,
}: {
  title: string;
  description?: string;
  /** Explains the row behind a question mark next to the title */
  help?: string;
  /** Current value shown on the right, like a count or a name */
  value?: string;
  /** Indents the row under the switch it belongs to */
  nested?: boolean;
  /** Not unlocked for this module or caller; the page explains how to get it */
  locked?: boolean;
  /** Read-only: the value still shows, the row does not open anything */
  disabled?: boolean;
  /** Edits in a dialog instead of opening a page */
  opensDialog?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "hover:bg-background-neutral flex w-full items-center gap-4 px-4 py-3 text-left disabled:cursor-default disabled:hover:bg-transparent",
        nested && "pl-8",
      )}
    >
      <RowText title={title} description={description} help={help} />
      {value && !locked ? (
        <Text className="text-foreground-pressed max-w-56 shrink-0 truncate">
          {value}
        </Text>
      ) : null}
      {locked ? (
        <Lock className="text-foreground-pressed size-4 shrink-0" />
      ) : disabled ? null : opensDialog ? (
        <Pencil className="text-foreground-pressed size-4 shrink-0" />
      ) : (
        <ChevronRight className="text-foreground-pressed size-4 shrink-0" />
      )}
    </button>
  );
}

/** SwitchRow turns one thing on or off. */
export function SwitchRow({
  id,
  title,
  description,
  checked,
  disabled = false,
  onCheckedChange,
}: {
  id: string;
  title: string;
  description?: ReactNode;
  checked: boolean;
  disabled?: boolean;
  /** Leave it out for a switch that only shows a fixed state */
  onCheckedChange?: (next: boolean) => void;
}) {
  return (
    <div className="flex w-full items-center gap-4 px-4 py-3">
      <RowText title={title} description={description} htmlFor={id} />
      <Switch
        id={id}
        checked={checked}
        disabled={disabled || !onCheckedChange}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
}

/**
 * ControlRow puts a small control, like a short input, a select or a button,
 * on the right of the title.
 */
export function ControlRow({
  title,
  description,
  htmlFor,
  children,
}: {
  title: string;
  description?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex w-full items-center gap-4 px-4 py-3">
      <RowText title={title} description={description} htmlFor={htmlFor} />
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}

/**
 * BlockRow puts a wide control, like a text area, a list or a grid of cards,
 * under the title across the full row.
 */
export function BlockRow({
  title,
  description,
  htmlFor,
  help,
  action,
  children,
}: {
  title?: string;
  description?: ReactNode;
  htmlFor?: string;
  /** Explains the row behind a question mark next to the title */
  help?: string;
  /** Small control on the title line, like a generate or add button */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex w-full flex-col gap-3 px-4 py-3">
      {title ? (
        <div className="flex items-start gap-4">
          <RowText
            title={title}
            description={description}
            htmlFor={htmlFor}
            help={help}
          />
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/** RowNote is a plain line of text inside a card, like a hint or an empty state. */
export function RowNote({ children }: { children: ReactNode }) {
  return (
    <div className="px-4 py-3">
      <Text className="text-foreground-pressed">{children}</Text>
    </div>
  );
}

/**
 * RadioRow is one choice of a single-choice list: the row is the button, the
 * dot on the right shows which one is picked. Put the rows of one choice in
 * the same card, without boxes of their own. A leading control, like a
 * preview button, sits in the icon spot but stays its own button.
 */
export function RadioRow({
  icon,
  leading,
  title,
  description,
  checked,
  disabled = false,
  onSelect,
}: {
  icon?: ReactNode;
  /** An interactive control in place of the icon */
  leading?: ReactNode;
  title: string;
  description?: ReactNode;
  checked: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      className={cn(
        "hover:bg-background-neutral flex w-full items-center gap-4 px-4",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      {leading ? <span className="flex shrink-0">{leading}</span> : null}
      <button
        type="button"
        role="radio"
        aria-checked={checked}
        disabled={disabled}
        onClick={onSelect}
        // focus ring only for keyboard use, not after a click
        className="focus-visible:ring-primary-500/40 flex min-w-0 flex-1 items-center gap-4 rounded-md py-3 text-left outline-none focus-visible:ring-2"
      >
        {icon ? (
          <span className="text-foreground-pressed flex size-5 shrink-0 items-center justify-center [&_img]:size-5 [&_svg]:size-5">
            {icon}
          </span>
        ) : null}
        <RowText title={title} description={description} />
        <span
          aria-hidden
          className={cn(
            "flex size-5 shrink-0 items-center justify-center rounded-full border-2",
            checked ? "border-primary-500" : "border-border-pressed",
          )}
        >
          {checked ? (
            <span className="bg-primary-500 size-2.5 rounded-full" />
          ) : null}
        </span>
      </button>
    </div>
  );
}
